/**
 * 🔒 Confidentiality Roles (piece 8; piece 13's assignment screen; piece 15's
 * management screen; T2352). A `ConfidentialityRole` is a named permission in
 * its own right, never a `SystemRole`, and a `ConfidentialityRoleGrant` names
 * the person-scoped `NDAAgreement` that backs it. Settings lists and edits the
 * roles; the Person page grants and ends them. Both read through the routes
 * below, relative to the API root.
 */

/** The Settings page. */
export const CONFIDENTIALITY_ROLES_ROUTE = '/settings/people/confidentiality-roles';
/** The roles' list and their add. */
export const CONFIDENTIALITY_ROLES_PATH = '/confidentiality-roles';

/** One role's edit, its deactivation and its reactivation. */
export function confidentialityRolePath(roleId: string): string {
  return `${CONFIDENTIALITY_ROLES_PATH}/${encodeURIComponent(roleId)}`;
}

/** A person's grants, the roles and agreements the window offers, and a new grant. */
export function personConfidentialityGrantsPath(personId: string): string {
  return `/people/${encodeURIComponent(personId)}/confidentiality-grants`;
}

/** One grant's End. */
export function confidentialityGrantEndPath(personId: string, grantId: string): string {
  return `${personConfidentialityGrantsPath(personId)}/${encodeURIComponent(grantId)}/end`;
}

/** The longest role name the window takes. */
export const CONFIDENTIALITY_ROLE_NAME_MAX = 120;

/** One role as Settings lists it. */
export interface ConfidentialityRoleRow {
  id: string;
  version: number;
  name: string;
  description: string | null;
  isActive: boolean;
  /** How many grants of the role are open now, so a deactivation is read against its use. */
  openGrants: number;
}

/** What the add window and the row's pencil send. */
export interface ConfidentialityRoleDraft {
  name: string;
  description: string;
}

/** The refusal a draft meets before anything is sent, in the sentence the server answers with; null passes. */
export function confidentialityRoleProblem(draft: ConfidentialityRoleDraft): string | null {
  const name = draft.name.trim();
  if (!name) return 'A confidentiality role has a name.';
  if (name.length > CONFIDENTIALITY_ROLE_NAME_MAX) return `A confidentiality role's name is at most ${CONFIDENTIALITY_ROLE_NAME_MAX} characters.`;
  return null;
}

/**
 * 🔒 The backing agreement's state beside each role (piece 8; piece 13): an
 * assignment that silently does nothing because its agreement lapsed is worse
 * than no assignment. Active reads Expired past its expiry day and Upcoming
 * before its effective day, on the Organization's day.
 */
export const GRANT_AGREEMENT_STATES = ['Active', 'Upcoming', 'Draft', 'Demanded', 'Expired', 'Revoked'] as const;
export type GrantAgreementState = (typeof GRANT_AGREEMENT_STATES)[number];

/** The state an agreement reads on a day, both ISO calendar days (`YYYY-MM-DD`). */
export function grantAgreementState(agreement: { status: 'Draft' | 'Demanded' | 'Active' | 'Expired' | 'Revoked'; effectiveDate: string; expiryDate: string | null }, day: string): GrantAgreementState {
  if (agreement.status !== 'Active') return agreement.status;
  if (agreement.expiryDate !== null && agreement.expiryDate < day) return 'Expired';
  if (agreement.effectiveDate > day) return 'Upcoming';
  return 'Active';
}

/** Which of the four status groups a state's pill reads in (piece 1); only Active opens a file. */
export function grantAgreementGroup(state: GrantAgreementState): 'good' | 'warning' | 'error' {
  if (state === 'Active') return 'good';
  if (state === 'Expired' || state === 'Revoked') return 'error';
  return 'warning';
}

/** One person-scoped agreement the grant window offers. */
export interface GrantAgreementOption {
  id: string;
  effectiveDate: string;
  expiryDate: string | null;
  state: GrantAgreementState;
}

/** One grant as the Person page's tile draws it. */
export interface ConfidentialityGrantRow {
  id: string;
  roleId: string;
  roleName: string;
  agreement: GrantAgreementOption;
  grantedByName: string;
  grantedAt: string;
  endedAt: string | null;
}

/** The Person page's read: every grant, open first, and what the grant window offers. */
export interface PersonConfidentialityView {
  rows: ConfidentialityGrantRow[];
  /** The active roles; the window leaves out the ones this person already holds open. */
  roles: { id: string; name: string }[];
  agreements: GrantAgreementOption[];
  /** Whether this viewer grants and ends: the administrative capability (piece 8). */
  writes: boolean;
}

/** What a grant sends; a missing agreement is sent as null, so the service's own refusal is what the person reads. */
export interface ConfidentialityGrantDraft {
  confidentialityRoleId: string;
  ndaAgreementId: string | null;
}
